import styled from '@emotion/styled';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ServerError from '../../../../../components/ServerError';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { useFlairLocalStorage } from '../../../hooks/useFlairLocalStorage';
import { SelectSingle } from '../../../components/Selects/SelectSingle';
import SkeletonCard from '../../../components/Skeleton/Card';
import { SkillsRadarChart } from '../radarChart/SkillsRadarChart';
import { useEmployeeSkills } from '../common/useEmployeeAvailableSkills';
import { useUpdateSkillTop } from '../common/useEmployeeSkillsMutations';
import { buildSkillGroups } from '../common/skillGrouping';
import { mapChartSkill } from './mappings';
import { RadarGroupingToggle } from './RadarGroupingToggle';
import { SkillChartLabel } from '../radarChart/SkillChartLabel';
import { formatMonthsExperience } from '../common/monthsExperience';
import { MAX_TOP_SKILLS_NUMBER } from '../common/types';

type Props = {
  employeeId: string;
};

const MAX_SKILLS_SHOWN = MAX_TOP_SKILLS_NUMBER;
const MIN_SKILLS_SHOWN = 2;
const MIN_SPOKES = 3;

// TODO: Refactor: extract EmployeeSkillsRadar and EmployeeSkillsRadarConnected
export const EmployeeSkillsRadar: React.FC<React.PropsWithChildren<Props>> = ({
  employeeId,
}) => {
  const t = useNamespacedTranslation('skills.employeeSkillCard');
  const tGrouping = useNamespacedTranslation('skills.grouping');
  const { t: tRoot } = useTranslation();
  const [grouping, setGrouping] = useFlairLocalStorage(
    'skills_radar_grouping',
    'skill',
  );

  const { data, loading, error } = useEmployeeSkills(employeeId);
  const { updateSkillTop } = useUpdateSkillTop();

  const resolveSkillName = useCallback(
    (skillId: string) =>
      data?.skills.find((s) => s.Id === skillId)?.localizedName,
    [data],
  );

  const chartSkills = useMemo(
    () =>
      data?.activeEmployees
        .flatMap((x) => x.employeeSkills)
        .filter((s) => s.flair__Rate__c),
    [data],
  );

  const allRatedSkills = useMemo(
    () => chartSkills?.map(mapChartSkill) ?? [],
    [chartSkills],
  );

  const availableSkills = useMemo(
    () => chartSkills?.filter((s) => !s.flair__Top__c).map(mapChartSkill) ?? [],
    [chartSkills],
  );

  const shownSkills = useMemo(() => {
    return chartSkills?.filter((s) => s.flair__Top__c).map(mapChartSkill) ?? [];
  }, [chartSkills]);

  const groupedSpokeCount = useMemo(() => {
    if (grouping === 'skill') {
      return 0;
    }
    return buildSkillGroups(allRatedSkills, grouping, resolveSkillName).length;
  }, [grouping, allRatedSkills, resolveSkillName]);

  const description = useMemo(() => {
    if (shownSkills.length === 0) {
      return t('threeSkillsToGo');
    }

    if (shownSkills.length === 1) {
      return t('twoSkillsToGo');
    }

    if (shownSkills.length === 2) {
      return t('oneSkillsToGo');
    }

    if (availableSkills.length + shownSkills.length > MIN_SKILLS_SHOWN) {
      return t('description');
    }
    return t('employeeHasNoRatedSkills');
  }, [availableSkills, shownSkills, t]);

  const handleSkillChange = useCallback(
    (id: string | null, isChosen: boolean) => {
      if (id) {
        updateSkillTop({
          id,
          employeeId,
          top: isChosen,
        });
      }
    },
    [updateSkillTop, employeeId],
  );

  if (error) {
    return <ServerError />;
  }
  if (loading) {
    return <SkeletonCard height={250} />;
  }

  const isGrouped = grouping !== 'skill';

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="small ms-1">{isGrouped ? null : description}</div>
        <RadarGroupingToggle value={grouping} onChange={setGrouping} />
      </div>

      {isGrouped ? (
        groupedSpokeCount >= MIN_SPOKES ? (
          <div className="mb-3">
            <SkillsRadarChart
              skills={allRatedSkills}
              grouping={grouping}
              resolveSkillName={resolveSkillName}
            />
          </div>
        ) : (
          <div className="small text-center py-4">
            {tGrouping('notEnoughGroups')}
          </div>
        )
      ) : (
        <>
          {shownSkills.length > MIN_SKILLS_SHOWN && (
            <div className="mb-3">
              <SkillsRadarChart skills={shownSkills} />
            </div>
          )}

          {availableSkills.length > 0 &&
            shownSkills.length < MAX_SKILLS_SHOWN && (
              <div className="mb-2">
                <SelectSingle
                  placeholder={t('filter.placeholder')}
                  options={availableSkills.map((s) => {
                    return { label: s.name, value: s.id };
                  })}
                  heightMode="normal"
                  className="small"
                  borderless={true}
                  value=""
                  onChange={(value) => {
                    handleSkillChange(value, true);
                  }}
                />
              </div>
            )}

          <LabelsContainer>
            {shownSkills.map((s) => (
              <SkillChartLabel
                key={s.id}
                label={s.name}
                experienceLabel={formatMonthsExperience(
                  s.monthsExperience,
                  tRoot,
                )}
                onClick={() => handleSkillChange(s.id, false)}
              />
            ))}
          </LabelsContainer>
        </>
      )}
    </>
  );
};

const LabelsContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
});
